import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Breadcrumb, Button, Card, Col, Empty, Input, Modal, Row, Select, Skeleton, Space, Table, Tree, Typography, message } from "antd";
import { FolderOutlined, FileOutlined, ReloadOutlined, ArrowLeftOutlined, EyeOutlined, DownloadOutlined } from "@ant-design/icons";
import {
  GraphDrive,
  GraphDriveItem,
  ResourceAccessConfig,
  ResourceTreeNode,
  getAccessConfigs,
  getDepartments,
  getDrives,
  getContentBlob,
  getThumbnailBlob,
  getMenu,
  getResourceTree,
  listByScope,
  searchInScope,
  updateAccessConfig,
  getMemoryDebugAccessibleChunks,
  ResourceScopeMenuItem,
} from "../services/resourcesApi";
import { useAppDispatch, useAppSelector } from "../store";
import { addHotMemoryItem } from "../store/hotMemorySlice";
import { addRecentResource } from "../store/recentResourcesSlice";

const { Title, Text } = Typography;

const Resources: React.FC = () => {
  const currentUser = useAppSelector((s) => s.user.currentUser);
  const isAdmin = !!currentUser?.accesses?.includes("admin");
  const isSupplierUser = !!currentUser?.departmentFlags?.isSupplier;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const hotMemoryUserId = currentUser?.id || "";
  const addHotMemory = (item: any) => {
    if (!hotMemoryUserId) return;
    dispatch(addHotMemoryItem({ userId: hotMemoryUserId, item }));
    message.success("Ajouté au contexte");
  };

  const recentItems = useAppSelector((s) => {
    const uid = currentUser?.id || "";
    const byUser = (s as any).recentResources?.itemsByUser || {};
    const items = (uid && Array.isArray(byUser[uid]) ? byUser[uid] : []) as any[];
    return items.map((it) => {
      const maybePath = typeof it?.path === 'string' ? it.path : '';
      const parts = maybePath ? maybePath.split('/').filter(Boolean) : [];
      const legacyFileName = parts.length ? parts[parts.length - 1] : '';
      const legacyFolder = parts.length > 1 ? parts.slice(0, -1).join('/') : '';
      return {
        ...it,
        fileName: it?.fileName || legacyFileName || it?.name,
        folderPath: it?.folderPath || legacyFolder || '',
      };
    });
  });

  useEffect(() => {
    if (isSupplierUser && !isAdmin) {
      message.error("Accès insuffisant pour cette ressource.");
      navigate("/");
    }
  }, [isSupplierUser, isAdmin, navigate]);

  const handleDebugMemory = async () => {
    try {
      const q = search.trim();
      const payload = await getMemoryDebugAccessibleChunks(q ? { q } : undefined);
      const usedSources = Array.isArray(payload?.used_sources) ? payload.used_sources : [];
      const resourcePaths = usedSources.length
        ? usedSources.map((src: any) => src?.resourcePath).filter(Boolean)
        : (Array.isArray(payload?.chunks) ? payload.chunks.map((c: any) => c?.resourcePath).filter(Boolean) : []);

      console.debug('[memory/debug] allowedRoots', payload?.allowedRoots || []);
      console.debug('[memory/debug] usedSources.length', usedSources.length);
      console.debug('[memory/debug] resourcePaths', resourcePaths);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur inconnue';
      message.error(`Debug memory impossible: ${msg}`);
    }
  };

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<GraphDriveItem[]>([]);
  const [search, setSearch] = useState<string>("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchItems, setSearchItems] = useState<GraphDriveItem[]>([]);
  const [folderStack, setFolderStack] = useState<Array<{ name: string }>>([]);
  const [menu, setMenu] = useState<ResourceScopeMenuItem[]>([]);
  const [menuLoading, setMenuLoading] = useState(false);
  const [selectedScope, setSelectedScope] = useState<string | null>(null);
  const [drivesModalOpen, setDrivesModalOpen] = useState(false);
  const [drivesLoading, setDrivesLoading] = useState(false);
  const [drives, setDrives] = useState<GraphDrive[]>([]);
  const [openLoadingId, setOpenLoadingId] = useState<string | null>(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTitle, setPreviewTitle] = useState<string>("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewType, setPreviewType] = useState<string>("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewMeta, setPreviewMeta] = useState<{ id: string; scope: string; path: string; name: string; mimeType?: string | null; webUrl?: string | null } | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [recentThumbUrls, setRecentThumbUrls] = useState<Record<string, string>>({});
  const [docThumbUrls, setDocThumbUrls] = useState<Record<string, string>>({});

  const [accessModalOpen, setAccessModalOpen] = useState(false);
  const [accessLoading, setAccessLoading] = useState(false);
  const [departments, setDepartments] = useState<string[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null);
  const [accessConfigs, setAccessConfigs] = useState<Record<string, ResourceAccessConfig>>({});
  const [pathAssignments, setPathAssignments] = useState<Record<string, "none" | "all" | "manager2" | "manager1">>({});
  const [treeData, setTreeData] = useState<any[]>([]);

  const currentSubPath = useMemo(() => folderStack.map((f) => f.name).filter(Boolean).join("/"), [folderStack]);

  const load = async (opts?: { reset?: boolean }) => {
    try {
      setLoading(true);
      if (!selectedScope) {
        setItems([]);
        return;
      }

      const result = await listByScope({
        scope: selectedScope,
        ...(currentSubPath ? { subPath: currentSubPath } : {}),
      });
      setItems(result.items);
      if (opts?.reset) {
        setSearch("");
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erreur inconnue";
      message.error(`Impossible de charger les ressources: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const buildRecentFullPath = (item: any) => {
    const folderPath = String(item.folderPath || "");
    const fileName = String(item.fileName || item.name || "");
    return folderPath ? `${folderPath}/${fileName}` : fileName;
  };

  const closePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewType('');
    setPreviewTitle('');
    setPreviewMeta(null);
    setPreviewError(null);
    setPreviewOpen(false);
  };

  const openPreview = async (params: { id: string; scope: string; path: string; name: string; mimeType?: string | null; webUrl?: string | null }) => {
    try {
      setPreviewLoading(true);
      setPreviewOpen(true);
      setPreviewTitle(params.name);
      setPreviewMeta(params);
      setPreviewError(null);

      if (previewUrl) URL.revokeObjectURL(previewUrl);

      const primary = await getContentBlob({ scope: params.scope, path: params.path, itemId: params.id });

      console.debug('[resources/preview] fetched blob', {
        scope: params.scope,
        path: params.path,
        name: params.name,
        mimeType: params.mimeType,
        contentType: primary.contentType,
        size: primary.blob.size,
      });

      const lowerName = String(params.name || '').toLowerCase();
      const lowerMime = String(params.mimeType || '').toLowerCase();
      const lowerCt = String(primary.contentType || '').toLowerCase();

      const isPdf = lowerMime === 'application/pdf' || lowerName.endsWith('.pdf') || lowerCt.includes('application/pdf');

      if (isPdf) {
        let isActuallyPdf = true;
        let headText = '';
        try {
          headText = await primary.blob.slice(0, 256).text();
          isActuallyPdf = headText.startsWith('%PDF-');
        } catch {
          // ignore
        }

        console.debug('[resources/preview] pdf check (primary)', {
          scope: params.scope,
          path: params.path,
          name: params.name,
          contentType: primary.contentType,
          headText: headText ? headText.slice(0, 120) : '',
          isActuallyPdf,
        });

        if (!isActuallyPdf) {
          try {
            const retry = await getContentBlob({ scope: params.scope, path: params.path, itemId: params.id, download: true });
            let retryHeadText = '';
            try {
              retryHeadText = await retry.blob.slice(0, 256).text();
            } catch {
              retryHeadText = '';
            }
            isActuallyPdf = retryHeadText.startsWith('%PDF-');

            console.debug('[resources/preview] pdf check (retry download)', {
              scope: params.scope,
              path: params.path,
              name: params.name,
              contentType: retry.contentType,
              headText: retryHeadText ? retryHeadText.slice(0, 120) : '',
              isActuallyPdf,
            });
            if (isActuallyPdf) {
              const normalizedBlob = retry.blob.slice(0, retry.blob.size, 'application/pdf');
              const url = URL.createObjectURL(normalizedBlob);
              setPreviewType('application/pdf');
              setPreviewUrl(url);
              return;
            }
          } catch {
            // ignore
          }
        }

        if (!isActuallyPdf) {
          setPreviewUrl(null);
          setPreviewType('');
          const ct = String(primary.contentType || 'unknown');
          const head = headText ? headText.slice(0, 80).replace(/\s+/g, ' ').trim() : '';
          setPreviewError(`Échec du chargement du document PDF (content-type=${ct}${head ? `, head=${head}` : ''})`);
          return;
        }
      }

      const normalizedBlob = isPdf ? primary.blob.slice(0, primary.blob.size, 'application/pdf') : primary.blob;
      const normalizedType = isPdf ? 'application/pdf' : primary.contentType;

      const url = URL.createObjectURL(normalizedBlob);
      setPreviewType(normalizedType);
      setPreviewUrl(url);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur inconnue';
      console.error('[resources/preview] openPreview failed', {
        scope: params.scope,
        path: params.path,
        name: params.name,
        mimeType: params.mimeType,
        error: err,
      });
      if (previewUrl) {
        try {
          URL.revokeObjectURL(previewUrl);
        } catch {
          // ignore
        }
      }
      setPreviewUrl(null);
      setPreviewType('');
      setPreviewError(msg);
      message.error(`Impossible d'afficher le fichier: ${msg}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  const touchRecent = (params: { id: string; scope: string; path: string; name: string; mimeType?: string | null }) => {
    if (!currentUser?.id) return;
    const normalizedPath = String(params.path || "");
    const parts = normalizedPath.split('/').filter(Boolean);
    const folderPath = parts.length > 1 ? parts.slice(0, -1).join('/') : '';
    const fileName = parts.length ? parts[parts.length - 1] : params.name;
    dispatch(
      addRecentResource({
        userId: currentUser.id,
        item: {
          id: params.id,
          name: params.name,
          scope: params.scope,
          folderPath,
          fileName,
          mimeType: params.mimeType || null,
        },
      })
    );
  };

  const downloadFile = async (params: { id: string; scope: string; path: string; name: string }) => {
    try {
      setOpenLoadingId(params.id);
      const { blob } = await getContentBlob({ scope: params.scope, path: params.path, itemId: params.id, download: true });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = params.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur inconnue';
      message.error(`Impossible de télécharger le fichier: ${msg}`);
    } finally {
      setOpenLoadingId(null);
    }
  };

  const loadMenu = async () => {
    try {
      setMenuLoading(true);
      const data = await getMenu();
      setMenu(data);

      const first = Array.isArray(data) && data.length > 0 ? data[0].scope : null;
      const stillAllowed = selectedScope ? data.some((d) => d.scope === selectedScope) : false;
      if (!first) {
        setSelectedScope(null);
      } else if (!selectedScope || !stillAllowed) {
        setSelectedScope(first);
      } else if (data.length === 1 && selectedScope !== first) {
        setSelectedScope(first);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erreur inconnue";
      message.error(`Impossible de charger le menu des ressources: ${msg}`);
    } finally {
      setMenuLoading(false);
    }
  };

  const openDrivesModal = async () => {
    try {
      setDrivesLoading(true);
      setDrivesModalOpen(true);
      const data = await getDrives();
      setDrives(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erreur inconnue";
      message.error(`Impossible de lister les drives: ${msg}`);
    } finally {
      setDrivesLoading(false);
    }
  };

  useEffect(() => {
    loadMenu();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAccessModal = async () => {
    try {
      setAccessLoading(true);
      setAccessModalOpen(true);

      const [deps, cfgs] = await Promise.all([getDepartments(), getAccessConfigs()]);
      setDepartments(deps);

      const map: Record<string, ResourceAccessConfig> = {};
      (cfgs || []).forEach((c) => {
        map[c.department] = c;
      });
      setAccessConfigs(map);

      if (!selectedDepartment && deps.length > 0) {
        setSelectedDepartment(deps[0]);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erreur inconnue";
      message.error(`Impossible de charger la configuration d'accès: ${msg}`);
    } finally {
      setAccessLoading(false);
    }
  };

  useEffect(() => {
    if (!accessModalOpen) return;
    let cancelled = false;

    (async () => {
      try {
        const root = await getResourceTree();
        if (cancelled) return;
        setTreeData(root.map((n) => mapTreeNode(n)));
      } catch {
        if (!cancelled) setTreeData([]);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessModalOpen]);

  useEffect(() => {
    if (!accessModalOpen) return;
    if (!selectedDepartment) return;

    const cfg = accessConfigs[selectedDepartment];
    const all = (cfg && (cfg.pathsAll || cfg.scopesAll)) || [];
    const m2 = (cfg && (cfg.pathsManager2 || cfg.scopesManager2)) || [];
    const m1 = (cfg && (cfg.pathsManager1 || cfg.scopesManager1)) || [];
    const allSet = new Set(all);
    const m2Set = new Set(m2);
    const m1Set = new Set(m1);

    const next: Record<string, "none" | "all" | "manager2" | "manager1"> = {};
    allSet.forEach((p) => (next[p] = "all"));
    m2Set.forEach((p) => {
      if (!next[p]) next[p] = "manager2";
    });
    m1Set.forEach((p) => {
      next[p] = "manager1";
    });
    setPathAssignments(next);
  }, [accessConfigs, accessModalOpen, selectedDepartment]);

  const mapTreeNode = (n: ResourceTreeNode) => {
    return {
      key: n.id,
      id: n.id,
      name: n.name,
      path: n.path,
      isLeaf: !n.hasChildren,
      title: n.name,
    };
  };

  const updateTreeData = (list: any[], key: React.Key, children: any[]): any[] =>
    list.map((node) => {
      if (node.key === key) {
        return {
          ...node,
          children,
        };
      }

      if (node.children) {
        return {
          ...node,
          children: updateTreeData(node.children, key, children),
        };
      }

      return node;
    });

  const renderTreeTitle = (node: any) => {
    const p = String(node?.path || "");

    const levelOrder: Record<string, number> = {
      none: 0,
      manager1: 1,
      manager2: 2,
      all: 3,
    };
    const formatLevel = (lvl: string) => {
      if (lvl === 'all') return 'All';
      if (lvl === 'manager2') return 'Manager 2';
      if (lvl === 'manager1') return 'Manager 1';
      return 'Aucun';
    };

    const direct = p ? (pathAssignments[p] || "none") : "none";
    const ancestor = p
      ? Object.entries(pathAssignments)
        .filter(([otherPath, otherLevel]) => {
          if (!otherPath || otherPath === p) return false;
          if (!otherLevel || otherLevel === 'none') return false;
          return p.startsWith(`${otherPath}/`);
        })
        .sort((a, b) => b[0].length - a[0].length)[0]
      : undefined;

    const descendantLevels = p
      ? Object.entries(pathAssignments)
        .filter(([otherPath, otherLevel]) => {
          if (!otherPath || otherPath === p) return false;
          if (!otherLevel || otherLevel === 'none') return false;
          return otherPath.startsWith(`${p}/`);
        })
        .map(([, otherLevel]) => String(otherLevel))
      : [];

    const descendantMax = descendantLevels.reduce((acc, lvl) => {
      return levelOrder[lvl] > levelOrder[acc] ? lvl : acc;
    }, 'none');

    const lockedByAncestor = !!ancestor;
    const lockedByDescendant = descendantMax !== 'none';
    const locked = lockedByAncestor || lockedByDescendant;
    const effectiveLevel = direct !== 'none' ? direct : lockedByAncestor ? String(ancestor?.[1] || 'none') : descendantMax;

    return (
      <Space style={{ width: "100%", justifyContent: "space-between" }}>
        <Text>{String(node?.name || node?.title || "")}</Text>
        {locked ? (
          <Text type="secondary">{formatLevel(effectiveLevel)}</Text>
        ) : (
          <Select
            size="small"
            value={direct}
            style={{ width: 140 }}
            onClick={(e) => e.stopPropagation()}
            onChange={(v) => {
              if (!p) return;

              if (v === "none") {
                setPathAssignments((prev) => {
                  const next = { ...prev };
                  delete next[p];
                  return next;
                });
                return;
              }

              const conflict = Object.entries(pathAssignments).find(([otherPath, otherLevel]) => {
                if (!otherPath || otherPath === p) return false;
                if (!otherLevel || otherLevel === "none") return false;
                return otherPath.startsWith(`${p}/`) || p.startsWith(`${otherPath}/`);
              });

              if (conflict) {
                const [otherPath, otherLevel] = conflict;
                message.error(
                  `Conflit: tu ne peux pas définir un droit sur "${p}" car "${otherPath}" a déjà un droit (${otherLevel}). Supprime d'abord l'autre règle.`
                );
                return;
              }

              setPathAssignments((prev) => ({
                ...prev,
                [p]: v,
              }));
            }}
            options={[
              { value: "none", label: "Aucun" },
              { value: "all", label: "All" },
              { value: "manager2", label: "Manager 2" },
              { value: "manager1", label: "Manager 1" },
            ]}
          />
        )}
      </Space>
    );
  };

  useEffect(() => {
    setFolderStack([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedScope]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedScope, currentSubPath]);

  useEffect(() => {
    if (!selectedScope) return;
    const q = search.trim();
    if (!q) {
      setSearchItems([]);
      setSearchLoading(false);
      return;
    }

    let cancelled = false;
    setSearchLoading(true);

    const t = window.setTimeout(async () => {
      try {
        const result = await searchInScope({ scope: selectedScope, subPath: currentSubPath, q });
        if (!cancelled) {
          setSearchItems(Array.isArray(result.items) ? result.items : []);
        }
      } catch (err) {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : 'Erreur inconnue';
          message.error(`Recherche impossible: ${msg}`);
        }
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, 150);

    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [search, selectedScope, currentSubPath]);

  const showingSearch = !!search.trim();
  const showingSearchSkeleton = showingSearch && searchLoading;

  const displayedItems = useMemo(() => {
    return showingSearch ? searchItems : items;
  }, [items, searchItems, showingSearch]);

  const folderItems = useMemo(() => displayedItems.filter((i) => i.isFolder), [displayedItems]);
  const fileItems = useMemo(() => displayedItems.filter((i) => !i.isFolder), [displayedItems]);

  const fileItemsKey = useMemo(
    () => fileItems.map((f) => `${f.id}:${f.mimeType || ''}:${f.name}:${String((f as any).path || '')}`).join('|'),
    [fileItems]
  );

  const goIntoFolder = (folder: GraphDriveItem) => {
    setFolderStack((prev) => [...prev, { name: folder.name }]);
  };

  const goBack = () => {
    setFolderStack((prev) => prev.slice(0, -1));
  };

  const goToIndex = (idx: number) => {
    setFolderStack((prev) => prev.slice(0, Math.max(0, idx)));
  };

  const buildFilePath = (fileName: string) => {
    return currentSubPath ? `${currentSubPath}/${fileName}` : fileName;
  };

  const openFile = async (file: GraphDriveItem, mode: 'web' | 'download', explicitPath?: string) => {
    if (!selectedScope) return;

    const path = explicitPath || buildFilePath(file.name);
    touchRecent({ id: file.id, scope: selectedScope, path, name: file.name, mimeType: file.mimeType || null });
    if (mode === 'web') {
      await openPreview({
        id: file.id,
        scope: selectedScope,
        path,
        name: file.name,
        mimeType: file.mimeType || null,
        webUrl: (file as any).webUrl || null,
      });
      return;
    }
    await downloadFile({ id: file.id, scope: selectedScope, path, name: file.name });
  };

  const goToFolderPath = (relativePath: string) => {
    const normalized = String(relativePath || '').replace(/^\/+/, '').replace(/\/+$/, '');
    const parts = normalized ? normalized.split('/').filter(Boolean) : [];
    setFolderStack(parts.map((name) => ({ name })));
  };

  const scopeCardItems = useMemo(() => {
    return menu.map((m) => ({
      scope: m.scope,
      label: m.label,
      selected: selectedScope === m.scope,
    }));
  }, [menu, selectedScope]);

  const PREVIEW_HEIGHT = 140;

  const getDocBadgeFromNameAndMime = (name: string, mimeType?: string | null) => {
    const mime = String(mimeType || '').toLowerCase();
    const ext = String(name || '').split('.').pop()?.toUpperCase() || '';
    if (mime === 'application/pdf' || ext === 'PDF') return 'PDF';
    if (mime.includes('word') || ext === 'DOC' || ext === 'DOCX') return 'DOC';
    if (mime.includes('excel') || ext === 'XLS' || ext === 'XLSX' || ext === 'CSV') return ext || 'XLS';
    if (mime.includes('powerpoint') || ext === 'PPT' || ext === 'PPTX') return 'PPT';
    if (ext) return ext;
    return 'DOC';
  };

  const getDocBadge = (item: GraphDriveItem) => {
    return getDocBadgeFromNameAndMime(String(item.name || ''), item.mimeType);
  };

  const renderFilePreview = (item: GraphDriveItem) => {
    if (item.isFolder) {
      return (
        <div className="resource-folder-cover">
          <FolderOutlined className="resource-folder-icon" />
        </div>
      );
    }

    const thumb = docThumbUrls[item.id] || null;
    const badge = getDocBadge(item);

    if (thumb) {
      return (
        <div style={{ position: 'relative', width: '100%', height: PREVIEW_HEIGHT, borderRadius: 8, overflow: 'hidden' }}>
          <img
            src={thumb}
            alt={item.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
          <div className="resource-doc-badge">{badge}</div>
        </div>
      );
    }

    // Fallback visible sans dépendre du CSS (évite les "cards vides")
    return (
      <div
        className="resource-doc-cover"
        style={{
          width: '100%',
          height: PREVIEW_HEIGHT,
          borderRadius: 8,
          background: '#470b0e10',
          border: '1px solid rgba(0,0,0,0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="resource-doc-badge"
          style={{
            position: 'absolute',
            top: 8,
            left: 8,
            padding: '2px 8px',
            borderRadius: 6,
            background: '#fff',
            border: '1px solid rgba(71,11,14,0.35)',
            color: '#470b0e',
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          {badge}
        </div>
        <FileOutlined className="resource-doc-icon" style={{ fontSize: 42, color: '#470b0e' }} />
      </div>
    );
  };

  useEffect(() => {
    // Cleanup thumbnails when navigating to another scope/folder
    Object.values(docThumbUrls).forEach((u) => {
      try {
        URL.revokeObjectURL(u);
      } catch {
        // ignore
      }
    });
    setDocThumbUrls({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedScope, currentSubPath]);

  useEffect(() => {
    if (!selectedScope) return;
    let cancelled = false;

    const run = async () => {
      const next: Record<string, string> = {};
      for (const f of fileItems) {
        if (docThumbUrls[f.id]) continue;

        try {
          const p = typeof (f as any).path === 'string' && (f as any).path ? String((f as any).path) : buildFilePath(f.name);
          const thumb = await getThumbnailBlob({ scope: selectedScope, itemId: f.id, path: p, size: 'large' });
          if (!thumb) continue;
          if (cancelled) return;
          next[f.id] = URL.createObjectURL(thumb.blob);
        } catch {
          // ignore
        }
      }
      if (!cancelled && Object.keys(next).length) {
        setDocThumbUrls((prev) => ({ ...prev, ...next }));
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedScope, currentSubPath, fileItemsKey]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const next: Record<string, string> = {};

      for (const it of recentItems) {
        const key = `${it.scope}:${it.folderPath}:${it.fileName || it.name}`;
        if (recentThumbUrls[key]) {
          next[key] = recentThumbUrls[key];
          continue;
        }

        try {
          const path = buildRecentFullPath(it);
          const thumb = await getThumbnailBlob({ scope: it.scope, itemId: String(it.id || ''), path, size: 'large' });
          if (!thumb) continue;
          if (cancelled) return;
          next[key] = URL.createObjectURL(thumb.blob);
        } catch {
          // ignore
        }
      }

      if (!cancelled) {
        setRecentThumbUrls((prev) => ({ ...prev, ...next }));
      }
    };

    run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentItems.length]);

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <Space align="center" style={{ justifyContent: "space-between", width: "100%" }}>
          <Title level={2} style={{ margin: 0, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
            Ressources
          </Title>
          <Space>
            {isAdmin ? (
              <Button onClick={openDrivesModal}>Lister les bibliothèques (admin)</Button>
            ) : null}
            {isAdmin ? (
              <Button onClick={openAccessModal} loading={accessLoading}>
                Configuration accès (admin)
              </Button>
            ) : null}
            <Button icon={<ReloadOutlined />} onClick={() => load()} loading={loading}>
              Rafraîchir
            </Button>
            <Button onClick={handleDebugMemory}>Debug memory</Button>
          </Space>
        </Space>

        <Card>
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            {menuLoading ? (
              <Skeleton active title={false} paragraph={{ rows: 1 }} />
            ) : menu.length === 0 ? (
              <div
                style={{
                  padding: '40px 24px',
                  borderRadius: 16,
                  background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)',
                  border: '1px dashed #c7d2fe',
                  textAlign: 'center',
                }}
              >
                <Text strong style={{ display: 'block', fontSize: 16, color: '#1e293b' }}>
                  Aucune ressource disponible
                </Text>
                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                  Ton compte n’a pas encore accès à une bibliothèque. Contacte un admin pour ouvrir un scope.
                </Text>
              </div>
            ) : menu.length === 1 ? (
              <Space direction="vertical" size={2}>
                <Text strong style={{ fontSize: 16, textTransform: 'capitalize' }}>{menu[0].label}</Text>
              </Space>
            ) : (
              <Row gutter={[12, 12]}>
                {scopeCardItems.map((m) => (
                  <Col key={m.scope} xs={24} sm={12} md={8} lg={6}>
                    <Card
                      hoverable
                      className="resource-card"
                      onClick={() => setSelectedScope(m.scope)}
                      style={{
                        borderColor: m.selected ? "#1677ff" : undefined,
                        boxShadow: m.selected ? "0 0 0 2px rgba(22,119,255,0.15)" : undefined,
                      }}
                    >
                      <Space direction="vertical" size={4} style={{ width: "100%" }}>
                        <Text strong>{m.label}</Text>
                        <Text type="secondary">Ouvrir</Text>
                      </Space>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}

            {menu.length === 0 ? null : (
              <>
                <Space style={{ justifyContent: "space-between", width: "100%" }}>
                  <Breadcrumb>
                    <Breadcrumb.Item>
                      <Button type="link" style={{ padding: 0 }} onClick={() => goToIndex(0)} disabled={!selectedScope}>
                        {selectedScope ? "Racine" : "Sélectionner une catégorie"}
                      </Button>
                    </Breadcrumb.Item>
                    {folderStack.map((p, idx) => (
                      <Breadcrumb.Item key={`${p.name}-${idx}`}>
                        <Button type="link" style={{ padding: 0 }} onClick={() => goToIndex(idx + 1)}>
                          {p.name}
                        </Button>
                      </Breadcrumb.Item>
                    ))}
                  </Breadcrumb>

                  <Space>
                    <Button icon={<ArrowLeftOutlined />} disabled={folderStack.length === 0} onClick={goBack}>
                      Retour
                    </Button>
                  </Space>
                </Space>

                <Input
                  placeholder="Rechercher dans ce dossier"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ maxWidth: 360 }}
                />
              </>
            )}

            {menu.length === 0 ? null : !selectedScope ? (
              <Empty description="Sélectionne une catégorie pour afficher les ressources." />
            ) : loading ? (
              <Space direction="vertical" size={16} style={{ width: "100%" }}>
                <Skeleton active title paragraph={{ rows: 2 }} />
                <Row gutter={[12, 12]}>
                  {Array.from({ length: 8 }).map((_, idx) => (
                    <Col key={idx} xs={24} sm={12} md={8} lg={6}>
                      <Card>
                        <Skeleton active title={false} paragraph={{ rows: 3 }} />
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Space>
            ) : showingSearchSkeleton ? (
              <Row gutter={[12, 12]}>
                {Array.from({ length: 8 }).map((_, idx) => (
                  <Col key={idx} xs={24} sm={12} md={8} lg={6}>
                    <Card>
                      <Skeleton active title={false} paragraph={{ rows: 3 }} />
                    </Card>
                  </Col>
                ))}
              </Row>
            ) : folderItems.length === 0 && fileItems.length === 0 ? (
              <Empty description={showingSearch ? "Aucun résultat pour cette recherche." : "Ce dossier est vide ou tu n'as pas accès à son contenu."} />
            ) : (
              <Space direction="vertical" size={16} style={{ width: "100%", gap: '4rem' }}>
                {folderItems.length > 0 ? (
                  <div>
                    <Text strong>Dossiers</Text>
                    <Row gutter={[12, 12]} style={{ marginTop: 8 }}>
                      {folderItems.map((f) => (
                        <Col key={f.id} xs={24} sm={12} md={8} lg={6}>
                          <Card
                            hoverable
                            className="resource-card resource-folder-card cursor-pointer"
                            onClick={() => {
                              if (showingSearch && typeof (f as any).path === 'string' && (f as any).path) {
                                goToFolderPath(String((f as any).path));
                                setSearch('');
                                return;
                              }
                              goIntoFolder(f);
                            }}
                          >
                            {renderFilePreview(f)}
                            <div style={{ marginTop: 10 }}>
                              <Text strong style={{ display: "block" }}>
                                {f.name}
                              </Text>
                              <Text type="secondary">
                                {typeof f.childCount === 'number' ? `${f.childCount} éléments` : 'Dossier'}
                              </Text>
                            </div>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </div>
                ) : null}

                {fileItems.length > 0 ? (
                  <div>
                    <Text strong>Documents</Text>
                    <Row gutter={[12, 12]} style={{ marginTop: 8 }}>
                      {fileItems.map((file) => (
                        <Col key={file.id} xs={24} sm={12} md={8} lg={6}>
                          <Card
                            hoverable
                            className="resource-card"
                            onClick={() => {
                              const p = typeof (file as any).path === 'string' ? String((file as any).path) : '';
                              openFile(file, 'web', p || undefined);
                            }}
                            styles={{ body: { padding: 12 } }}
                          >
                            {renderFilePreview(file)}
                            <div style={{ marginTop: 10 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                                <Text
                                  strong
                                  style={{ display: 'block', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                >
                                  {file.name}
                                </Text>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                                  <Button
                                    size="small"
                                    className="resource-icon-btn"
                                    icon={<EyeOutlined />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const p = typeof (file as any).path === 'string' ? String((file as any).path) : '';
                                      openFile(file, 'web', p || undefined);
                                    }}
                                    loading={openLoadingId === file.id}
                                  />
                                  <Button
                                    size="small"
                                    className="resource-icon-btn"
                                    icon={<DownloadOutlined />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const p = typeof (file as any).path === 'string' ? String((file as any).path) : '';
                                      openFile(file, 'download', p || undefined);
                                    }}
                                    loading={openLoadingId === file.id}
                                  />
                                </div>
                              </div>
                            </div>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </div>
                ) : null}

                {recentItems.length > 0 ? (
                  <div>
                    <Text strong>Récemment consultés</Text>
                    <Row gutter={[12, 12]} style={{ marginTop: 8 }}>
                      {recentItems.map((it) => {
                        const folderLabel = `${it.scope}${it.folderPath ? `/${it.folderPath}` : ''}`;
                        const key = `${it.scope}:${it.folderPath}:${it.fileName || it.name}`;
                        const thumb = recentThumbUrls[key];
                        const badge = getDocBadgeFromNameAndMime(String(it?.name || ''), it?.mimeType);
                        return (
                          <Col key={`${it.scope}:${it.folderPath}:${it.fileName || it.name}`} xs={24} sm={12} md={8} lg={6}>
                            <Card
                              hoverable
                              className="resource-card"
                              onClick={() => {
                                const path = buildRecentFullPath(it);
                                const meta = { id: it.id, scope: it.scope, path, name: it.name, mimeType: it.mimeType };
                                touchRecent(meta);
                                openPreview(meta);
                              }}
                              styles={{ body: { padding: 12 } }}
                            >
                              {thumb ? (
                                <div style={{ position: 'relative', width: '100%', height: PREVIEW_HEIGHT, borderRadius: 8, overflow: 'hidden' }}>
                                  <img
                                    src={thumb}
                                    alt={it.name}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                  />
                                  <div className="resource-doc-badge">{badge}</div>
                                </div>
                              ) : (
                                <div
                                  className="resource-doc-cover"
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: PREVIEW_HEIGHT,
                                    color: 'rgba(0,0,0,0.45)',
                                  }}
                                >
                                  <div className="resource-doc-badge">{badge}</div>
                                  <FileOutlined style={{ fontSize: 40 }} />
                                </div>
                              )}
                              <div style={{ marginTop: 10 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                                  <Text
                                    strong
                                    style={{ display: 'block', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                  >
                                    {it.name}
                                  </Text>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                                    <Button
                                      size="small"
                                      className="resource-icon-btn"
                                      icon={<EyeOutlined />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const path = buildRecentFullPath(it);
                                        const meta = { id: it.id, scope: it.scope, path, name: it.name, mimeType: it.mimeType };
                                        touchRecent(meta);
                                        openPreview(meta);
                                      }}
                                      loading={openLoadingId === it.id}
                                    />
                                    <Button
                                      size="small"
                                      className="resource-icon-btn"
                                      icon={<DownloadOutlined />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const path = buildRecentFullPath(it);
                                        const meta = { id: it.id, scope: it.scope, path, name: it.name, mimeType: it.mimeType };
                                        touchRecent(meta);
                                        downloadFile(meta);
                                      }}
                                      loading={openLoadingId === it.id}
                                    />
                                  </div>
                                </div>
                                <Text type="secondary" style={{ display: "block", fontSize: 12, marginTop: 4 }}>
                                  {folderLabel}
                                </Text>
                              </div>
                            </Card>

                          </Col>
                        );
                      })}
                    </Row>
                  </div>
                ) : null}
              </Space>
            )}
          </Space>
        </Card>

        <Modal
          title={previewTitle || 'Aperçu'}
          open={previewOpen}
          onCancel={() => {
            closePreview();
            setPreviewMeta(null);
          }}
          footer={
            previewMeta ? (
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button
                  onClick={() =>
                    addHotMemory({
                      id: previewMeta.id,
                      type: "resourceFile",
                      label: previewMeta.name,
                      data: {
                        scope: previewMeta.scope,
                        path: previewMeta.path,
                        name: previewMeta.name,
                        mimeType: previewMeta.mimeType,
                      },
                    })
                  }
                >
                  Ajouter au contexte
                </Button>
                {previewMeta.webUrl ? (
                  <Button
                    onClick={() => window.open(String(previewMeta.webUrl), '_blank', 'noopener,noreferrer')}
                  >
                    Ouvrir
                  </Button>
                ) : null}
                <Button
                  className="resource-icon-btn"
                  icon={<DownloadOutlined />}
                  onClick={() => downloadFile(previewMeta)}
                  loading={openLoadingId === previewMeta.id}
                />
              </Space>
            ) : null
          }
          width={900}
          destroyOnClose
        >
          {previewLoading ? (
            <Skeleton active />
          ) : previewError ? (
            <Empty description={previewError} />
          ) : !previewUrl ? (
            <Empty description="Aperçu indisponible" />
          ) : previewType.startsWith('image/') ? (
            <img
              src={previewUrl}
              alt={previewTitle}
              style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 8, display: 'block' }}
            />
          ) : previewType === 'application/pdf' || String(previewTitle || '').toLowerCase().endsWith('.pdf') ? (
            <object data={previewUrl} type="application/pdf" style={{ width: '100%', height: '70vh', border: 'none', borderRadius: 8 }}>
              <embed src={previewUrl} type="application/pdf" style={{ width: '100%', height: '70vh', border: 'none', borderRadius: 8 }} />
            </object>
          ) : (
            <iframe
              src={previewUrl}
              title={previewTitle}
              style={{ width: '100%', height: '70vh', border: 'none', borderRadius: 8 }}
            />
          )}
        </Modal>

        <Modal
          title="Bibliothèques de documents (drives)"
          open={drivesModalOpen}
          onCancel={() => setDrivesModalOpen(false)}
          footer={null}
          width={900}
        >
          <Table
            rowKey="id"
            loading={drivesLoading}
            pagination={false}
            dataSource={drives}
            columns={[
              {
                title: "Nom",
                dataIndex: "name",
                key: "name",
              },
              {
                title: "Drive ID",
                dataIndex: "id",
                key: "id",
                render: (value: string) => (
                  <Space>
                    <Text code>{value}</Text>
                    <Button
                      size="small"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(value);
                          message.success("Drive ID copié");
                        } catch {
                          message.error("Impossible de copier automatiquement. Copie manuelle nécessaire.");
                        }
                      }}
                    >
                      Copier
                    </Button>
                  </Space>
                ),
              },
              {
                title: "Type",
                dataIndex: "driveType",
                key: "driveType",
                width: 160,
              },
              {
                title: "Lien",
                dataIndex: "webUrl",
                key: "webUrl",
                render: (value: string | undefined) =>
                  value ? (
                    <Button size="small" onClick={() => window.open(value, "_blank", "noopener,noreferrer")}>
                      Ouvrir
                    </Button>
                  ) : null,
              },
            ]}
          />

          <div style={{ marginTop: 12 }}>
            <Text type="secondary">
              Une fois le bon Drive ID identifié, mets-le dans le <Text code>backend/.env</Text> (variable <Text code>MICROSOFT_DRIVE_ID</Text>).
            </Text>
          </div>
        </Modal>

        <Modal
          title="Configuration des accès (Ressources)"
          open={accessModalOpen}
          onCancel={() => setAccessModalOpen(false)}
          width={1000}
          okText="Enregistrer"
          confirmLoading={accessLoading}
          onOk={async () => {
            if (!selectedDepartment) return;
            try {
              setAccessLoading(true);
              const pathsAll: string[] = [];
              const pathsManager2: string[] = [];
              const pathsManager1: string[] = [];

              Object.entries(pathAssignments).forEach(([path, v]) => {
                if (v === "all") pathsAll.push(path);
                else if (v === "manager2") pathsManager2.push(path);
                else if (v === "manager1") pathsManager1.push(path);
              });

              const updated = await updateAccessConfig({
                department: selectedDepartment,
                pathsAll,
                pathsManager2,
                pathsManager1,
              });

              setAccessConfigs((prev) => ({ ...prev, [selectedDepartment]: updated }));
              message.success("Configuration enregistrée");
            } catch (err) {
              const msg = err instanceof Error ? err.message : "Erreur inconnue";
              message.error(`Impossible d'enregistrer: ${msg}`);
            } finally {
              setAccessLoading(false);
            }
          }}
        >
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Text strong>Équipes</Text>
              <div style={{ marginTop: 8 }}>
                <Select
                  style={{ width: "100%" }}
                  placeholder="Sélectionner une équipe"
                  value={selectedDepartment || undefined}
                  onChange={(v) => setSelectedDepartment(v)}
                  options={departments.map((d) => ({ value: d, label: d }))}
                />
              </div>

              <div style={{ marginTop: 12 }}>
                <Text type="secondary">
                  Règles:
                  <br />
                  All = employé + manager2 + manager1
                  <br />
                  Manager2 = manager2 + manager1
                  <br />
                  Manager1 = manager1 uniquement
                </Text>
              </div>
            </Col>

            <Col xs={24} md={16}>
              <Text strong>Arborescence (dossiers)</Text>
              <Tree
                style={{ marginTop: 8 }}
                blockNode
                treeData={treeData}
                titleRender={renderTreeTitle}
                loadData={async (node: any) => {
                  if (!node || node.children) return;
                  const parentId = node.id as string;
                  const parentPath = node.path as string;
                  const children = await getResourceTree({ parentId, parentPath });
                  setTreeData((orig) => updateTreeData(orig, node.key, children.map((c) => mapTreeNode(c))));
                }}
              />
            </Col>
          </Row>
        </Modal>
      </Space>
    </div>
  );
};

export default Resources;
